import { Card } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingBars from '../custom-components/LoadingBars';
import { GlobalContext } from '../global-context';
import { allPages } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import General from '../utils/GeneralUtils';
import './ServiceLimits.scss';

// Define the order for main service limits
const MAIN_SERVICE_LIMITS = [
  'NR_CONTACTS_RETURNED__MONTHLY',
  'NR_CONTACTS_RETURNED__ANNUAL',
  'NR_EMAIL_VERIFICATIONS__MONTHLY',
  'CSV_EXPORT_NR_CONTACTS__ONEOFF',
  'CSV_EXPORT_NR_INSTITUTIONS__ONEOFF'
];

// Define limits to exclude from display (either hidden or handled specially)
const EXCLUDED_LIMITS = [
  'NR_UNLOCKED_INSTITUTIONS'  // Shown as first main limit for account-based billing teams
];

/** A counted limit's current period, as the progress bars show it. */
interface LimitUsage {
  current: number;
  limit: number;
  percentage: number;
  resetDate: string;
}

/**
 * The usage of a limit that carries it (`used` and a `value`), else null. The percentage is
 * rounded to two places and the reset date is the day (YYYY-MM-DD), as Nest returned them.
 */
function usageOf(limit: Edulead.EduleadV4Limit | undefined): LimitUsage | null {
  if (limit?.used == null || limit.value == null) return null;
  const current = Number(limit.used);
  const max = Number(limit.value);
  return {
    current,
    limit: max,
    percentage: max > 0 ? Math.round((current / max) * 10000) / 100 : 0,
    resetDate: limit.reset_time?.slice(0, 10) ?? '',
  };
}

export function ServiceLimits() {
  const globalContext = useContext(GlobalContext);
  const [serviceLimits, setServiceLimits] = useState<Edulead.EduleadV4Limit[]>();

  const isAccountBasedBilling = globalContext?.authInfo?.team?.account_based_billing ?? false;

  useEffect(() => {
    General.setTitle(document, 'Service Limits', globalContext);
  }, [globalContext]);

  useEffect(() => {
    if (globalContext?.authInfo?.teamId) {
      loadServiceLimits();
    }
  }, [globalContext.authInfo?.teamId]);

  async function loadServiceLimits() {
    if (!globalContext?.authInfo?.teamId) return;

    try {
      setServiceLimits(await allPages(eduleadV4.limits.list({ page_size: 100 })));
    } catch (error) {
      console.error('Failed to load service limits:', error);
    }
  }

  // Separate main limits from other limits
  function separateLimits(limits: Edulead.EduleadV4Limit[]) {
    const mainLimits: Edulead.EduleadV4Limit[] = [];
    const otherLimits: Edulead.EduleadV4Limit[] = [];

    // First, add main limits in the defined order
    MAIN_SERVICE_LIMITS.forEach(limitName => {
      const limit = limits.find(l => l.id === limitName);
      if (limit) {
        mainLimits.push(limit);
      }
    });

    // Then, add remaining limits to other section (excluding hidden limits)
    limits.forEach(limit => {
      if (!MAIN_SERVICE_LIMITS.includes(limit.id ?? '') && !EXCLUDED_LIMITS.includes(limit.id ?? '')) {
        otherLimits.push(limit);
      }
    });

    return { mainLimits, otherLimits };
  }

  function getServiceLimitName(limitName: string) {
    return {
      'NR_UNLOCKED_INSTITUTIONS': 'Unlocked Accounts',
      'NR_CONTACTS_RETURNED__MONTHLY': 'Monthly Contact Search Limit',
      'NR_CONTACTS_RETURNED__ANNUAL': 'Annual Contact Search Limit',
      'CSV_EXPORT_NR_CONTACTS__ONEOFF': 'Contact Export Size Limit',
      'CSV_EXPORT_NR_INSTITUTIONS__ONEOFF': 'Institution Export Size Limit',
      'NR_FOIA_REQUESTS__TOTAL': 'Total FOIA Requests',
      'NR_EMAIL_VERIFICATIONS__MONTHLY': 'Monthly Email Verifications',
      'NR_BEACONS__TOTAL': 'Beacons',
      'BEACON_NR_INSTITUTIONS__MAX': 'Institutions per Beacon',
    }?.[limitName] || limitName;
  }

  function getServiceLimitDescription(limitName: string): React.ReactNode {
    const descriptions: Record<string, React.ReactNode> = {
      'NR_CONTACTS_RETURNED__MONTHLY': (
        <span className="desc-2">
          Contacts returned from searches and exports per month. We do not double count contacts. Resets on the 1st.
        </span>
      ),
      'NR_CONTACTS_RETURNED__ANNUAL': (
        <span className="desc-2">
          Contacts returned from searches and exports per year. We do not double count contacts. Resets on your subscription anniversary.
        </span>
      ),
      'CSV_EXPORT_NR_CONTACTS__ONEOFF': (
        <span className="desc-2">
          Maximum contacts per export (CSV or JSON).
        </span>
      ),
      'CSV_EXPORT_NR_INSTITUTIONS__ONEOFF': (
        <span className="desc-2">
          Maximum institutions per export (CSV or JSON).
        </span>
      ),
      'NR_UNLOCKED_INSTITUTIONS': (
        <span className="desc-2">
          Unique institutions unlocked through contact exports.
        </span>
      ),
      'NR_EMAIL_VERIFICATIONS__MONTHLY': (
        <span className="desc-2">
          Emails re-validated in contact exports per month. Every email in a re-validated export counts. Resets on the 1st.
        </span>
      ),
      'NR_BEACONS__TOTAL': (
        <span className="desc-2">
          Beacons the team holds (Settings → Beacons).
        </span>
      ),
      'BEACON_NR_INSTITUTIONS__MAX': (
        <span className="desc-2">
          Maximum institutions in one beacon.
        </span>
      ),
    };

    return descriptions[limitName] || null;
  }

  const { mainLimits, otherLimits } = serviceLimits ? separateLimits(serviceLimits) : { mainLimits: [], otherLimits: [] };
  const billableAccountsUsage = isAccountBasedBilling
    ? usageOf(serviceLimits?.find(l => l.id === 'NR_UNLOCKED_INSTITUTIONS'))
    : null;

  return (
    <GlobalContext.Consumer>
      {() => (
        <div className="content content-800 service-limits-page">
          <h2>Main Service Limits</h2>
          <Card variation='outlined' className="service-limits-page__card">
            {
              serviceLimits ? (
                <div className="service-limits-page__limits-container">
                  {/* Unlocked institutions limit - first item for account-based billing teams */}
                  {isAccountBasedBilling && billableAccountsUsage && (
                    <div className="service-limits-page__limit-item">
                      <div className="service-limits-page__limit-header">
                        <strong className="service-limits-page__limit-name">
                          <Link to="/settings/service-limits/unlocked-institutions" style={{ color: 'inherit', textDecoration: 'none' }}>
                            {getServiceLimitName('NR_UNLOCKED_INSTITUTIONS')}
                          </Link>
                        </strong>
                        <strong className="service-limits-page__limit-value">
                          {General.addCommasToNumber(billableAccountsUsage.limit)}
                        </strong>
                      </div>
                      <div className="service-limits-page__limit-description">
                        {getServiceLimitDescription('NR_UNLOCKED_INSTITUTIONS')}
                        {' '}
                        <Link to="/settings/service-limits/unlocked-institutions">
                          View all
                        </Link>
                      </div>
                      <progress
                        value={billableAccountsUsage.current}
                        max={billableAccountsUsage.limit}
                        className="service-limits-page__progress-bar progress-green"
                        data-tooltip-id="main-tooltip"
                        data-tooltip-content={`You have unlocked ${General.addCommasToNumber(billableAccountsUsage.current)} out of ${General.addCommasToNumber(billableAccountsUsage.limit)} institutions (${billableAccountsUsage.percentage.toFixed(1)}%).`}
                        data-tooltip-delay-show={0}
                      />
                    </div>
                  )}
                  {
                    mainLimits.map((limit: Edulead.EduleadV4Limit, index: number) => {
                      const id = limit.id ?? '';
                      const description = getServiceLimitDescription(id);
                      const isContactLimit = id === 'NR_CONTACTS_RETURNED__MONTHLY' || id === 'NR_CONTACTS_RETURNED__ANNUAL';
                      const isMonthlyEmailVerifications = id === 'NR_EMAIL_VERIFICATIONS__MONTHLY';
                      const contactUsage = isContactLimit ? usageOf(limit) : null;
                      const emailVerificationsUsage = isMonthlyEmailVerifications ? usageOf(limit) : null;
                      const periodLabel = limit.period === 'LIMIT_PERIOD_YEAR' ? 'annual' : 'monthly';

                      return (
                        <div key={index} className="service-limits-page__limit-item">
                          <div className="service-limits-page__limit-header">
                            <strong className="service-limits-page__limit-name">
                              {getServiceLimitName(id)}
                            </strong>
                            <strong className="service-limits-page__limit-value">
                              {limit.value != null ? General.addCommasToNumber(Number(limit.value)) : 'N/A'}
                            </strong>
                          </div>
                          {description && (
                            <div className="service-limits-page__limit-description">
                              {description}
                            </div>
                          )}
                          {isContactLimit && contactUsage && (
                            <progress
                              value={contactUsage.current}
                              max={contactUsage.limit}
                              className="service-limits-page__progress-bar progress-green"
                              data-tooltip-id="main-tooltip"
                              data-tooltip-content={`You have used ${General.addCommasToNumber(contactUsage.current)} out of ${General.addCommasToNumber(contactUsage.limit)} ${periodLabel} contacts (${contactUsage.percentage.toFixed(1)}%). Resets on ${contactUsage.resetDate}.`}
                              data-tooltip-delay-show={0}
                            />
                          )}
                          {isMonthlyEmailVerifications && emailVerificationsUsage && (
                            <progress
                              value={emailVerificationsUsage.current}
                              max={emailVerificationsUsage.limit}
                              className="service-limits-page__progress-bar progress-green"
                              data-tooltip-id="main-tooltip"
                              data-tooltip-content={`You have used ${General.addCommasToNumber(emailVerificationsUsage.current)} out of ${General.addCommasToNumber(emailVerificationsUsage.limit)} email verifications (${emailVerificationsUsage.percentage.toFixed(1)}%). Resets on ${emailVerificationsUsage.resetDate}.`}
                              data-tooltip-delay-show={0}
                            />
                          )}
                        </div>
                      );
                    })
                  }
                </div>
              ) : <LoadingBars numberOfRows={3} />
            }
          </Card>

          <h4 className="service-limits-page__section-title">Other Service Limits</h4>
          <Card variation='outlined' className="service-limits-page__other-card">
            {
              serviceLimits ? (
                <div className="service-limits-page__other-limits-container">
                  {
                    otherLimits.map((limit: Edulead.EduleadV4Limit, index: number) => {
                      return (
                        <div key={index} className="service-limits-page__other-limit-item">
                          <span>{getServiceLimitName(limit.id ?? '')}</span>
                          <span className="service-limits-page__other-limit-value">
                            {limit.value != null && General.addCommasToNumber(Number(limit.value))}
                          </span>
                        </div>
                      );
                    })
                  }
                </div>
              ) : <LoadingBars numberOfRows={5} />
            }
          </Card>
        </div>
      )}
    </GlobalContext.Consumer>
  );
}